import type {
	Attributes,
	OperationalEventPayload,
	ScreenEventPayload,
	TrackEventPayload,
	UIEventPayload,
} from '../events/analytics-web-client-types';
import {
	type BreadcrumbComponent,
	type BreadcrumbedEventChannel,
	type BreadcrumbMountedEventChannel,
	createBreadcrumbAPI,
	createBreadcrumbedEventChannel,
	type UseGetBreadcrumbsHook,
} from '../events/breadcrumbs';
import {
	createCatchingEventChannel,
	type Listener,
	type UseEventDispatchHook,
} from '../events/core';
import type { CaptureExceptionPayload } from '../events/sentry-browser/bridge';

export type {
	CaptureExceptionPayload,
	OperationalEventPayload,
	ScreenEventPayload,
	TrackEventPayload,
	UIEventPayload,
};

export type BreadcrumbAttributes = {
	// TODO: Standardize the attributes we send
	/**
	 * Just arbitrary set of metadata you can add to your breadcrumbs. ATM they get added directly to GasV3 attributes.
	 */
	attributes?: Record<any, any>;
	/**
	 * If set, this breadcrumb won't be used in any of the data/PM related analytics services.
	 */
	isForDevsOnly?: boolean;
};

const breadcrumbAPI = createBreadcrumbAPI<BreadcrumbAttributes>();
export const breadcrumbMountedEventChannel: BreadcrumbMountedEventChannel<BreadcrumbAttributes> =
	breadcrumbAPI.breadcrumbMountedEventChannel;
export const Breadcrumb: BreadcrumbComponent<BreadcrumbAttributes> = breadcrumbAPI.Breadcrumb;
export const useGetBreadcrumbs: UseGetBreadcrumbsHook<BreadcrumbAttributes> =
	breadcrumbAPI.useGetBreadcrumbs;

export const externalGasV3UIEventChannel: {
	Listener: Listener<{
		action: string;
		actionSubject: string;
		actionSubjectId?: string;
		attributes?: Attributes;
		containerId?: string;
		containerType?: string;
		objectId?: string;
		objectType?: string;
		source: string;
		tags?: string[];
	}>;
	useEventDispatch: UseEventDispatchHook<{
		action: string;
		actionSubject: string;
		actionSubjectId?: string;
		attributes?: Attributes;
		containerId?: string;
		containerType?: string;
		objectId?: string;
		objectType?: string;
		source: string;
		tags?: string[];
	}>;
} = createCatchingEventChannel<UIEventPayload>();
export const externalGasV3ScreenEventChannel: {
	Listener: Listener<ScreenEventPayload>;
	useEventDispatch: UseEventDispatchHook<ScreenEventPayload>;
} = createCatchingEventChannel<ScreenEventPayload>();
export const externalGasV3TrackEventChannel: {
	Listener: Listener<{
		action: string;
		actionSubject: string;
		actionSubjectId?: string;
		attributes?: Attributes;
		containerId?: string;
		containerType?: string;
		objectId?: string;
		objectType?: string;
		source: string;
		tags?: string[];
	}>;
	useEventDispatch: UseEventDispatchHook<{
		action: string;
		actionSubject: string;
		actionSubjectId?: string;
		attributes?: Attributes;
		containerId?: string;
		containerType?: string;
		objectId?: string;
		objectType?: string;
		source: string;
		tags?: string[];
	}>;
} = createCatchingEventChannel<TrackEventPayload>();
export const externalGasV3OperationalEventChannel: {
	Listener: Listener<{
		action: string;
		actionSubject: string;
		actionSubjectId?: string;
		attributes?: Attributes;
		containerId?: string;
		containerType?: string;
		objectId?: string;
		objectType?: string;
		source: string;
		tags?: string[];
	}>;
	useEventDispatch: UseEventDispatchHook<{
		action: string;
		actionSubject: string;
		actionSubjectId?: string;
		attributes?: Attributes;
		containerId?: string;
		containerType?: string;
		objectId?: string;
		objectType?: string;
		source: string;
		tags?: string[];
	}>;
} = createCatchingEventChannel<OperationalEventPayload>();

export const externalSentryExceptionChannel: {
	Listener: Listener<CaptureExceptionPayload>;
	useEventDispatch: UseEventDispatchHook<CaptureExceptionPayload>;
} = createCatchingEventChannel<CaptureExceptionPayload>();

export const internalGasV3UIEventChannel: BreadcrumbedEventChannel<
	Omit<
		{
			action: string;
			actionSubject: string;
			actionSubjectId?: string;
			attributes?: Attributes;
			containerId?: string;
			containerType?: string;
			objectId?: string;
			objectType?: string;
			source: string;
			tags?: string[];
		},
		'source'
	>,
	BreadcrumbAttributes
> = createBreadcrumbedEventChannel<Omit<UIEventPayload, 'source'>, BreadcrumbAttributes>(
	breadcrumbAPI,
	createCatchingEventChannel,
);
export const internalGasV3ScreenEventChannel: {
	Listener: Listener<ScreenEventPayload>;
	useEventDispatch: UseEventDispatchHook<ScreenEventPayload>;
} = createCatchingEventChannel<ScreenEventPayload>();
export const internalGasV3TrackEventChannel: BreadcrumbedEventChannel<
	Omit<
		{
			action: string;
			actionSubject: string;
			actionSubjectId?: string;
			attributes?: Attributes;
			containerId?: string;
			containerType?: string;
			objectId?: string;
			objectType?: string;
			source: string;
			tags?: string[];
		},
		'source'
	>,
	BreadcrumbAttributes
> = createBreadcrumbedEventChannel<Omit<TrackEventPayload, 'source'>, BreadcrumbAttributes>(
	breadcrumbAPI,
	createCatchingEventChannel,
);
export const internalGasV3OperationalEventChannel: BreadcrumbedEventChannel<
	Omit<
		{
			action: string;
			actionSubject: string;
			actionSubjectId?: string;
			attributes?: Attributes;
			containerId?: string;
			containerType?: string;
			objectId?: string;
			objectType?: string;
			source: string;
			tags?: string[];
		},
		'source'
	>,
	BreadcrumbAttributes
> = createBreadcrumbedEventChannel<Omit<OperationalEventPayload, 'source'>, BreadcrumbAttributes>(
	breadcrumbAPI,
	createCatchingEventChannel,
);

export const internalSentryExceptionChannel: BreadcrumbedEventChannel<
	CaptureExceptionPayload,
	BreadcrumbAttributes
> = createBreadcrumbedEventChannel<CaptureExceptionPayload, BreadcrumbAttributes>(
	breadcrumbAPI,
	createCatchingEventChannel,
);
